import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.white};
`;

const HeroSection = styled.section`
  width: 100%;
  height: 70vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${props => props.theme.colors.gradient};
  position: relative;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.white};
  margin-bottom: 2rem;
  text-align: center;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.5rem;
  font-weight: 400;
  color: ${props => props.theme.colors.white};
  margin-bottom: 2rem;
  text-align: center;
  max-width: 700px;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 1.2rem;
  }
`;

const BenefitsSection = styled.section`
  padding: 5rem 0;
`;

const BenefitsContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.dark};
  margin-bottom: 2rem;
  text-align: center;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const BenefitCard = styled(motion.div)`
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: ${props => props.theme.shadows.medium};
  background-color: ${props => props.theme.colors.white};
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-10px);
  }
`;

const BenefitIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.dark};
  margin-bottom: 1rem;
`;

const BenefitDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.dark};
`;

const RegistrationSection = styled.section`
  padding: 5rem 0;
  background-color: ${props => props.theme.colors.light};
`;

const RegistrationContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const RegisterButton = styled(motion.a)`
  display: inline-block;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  font-size: 1.2rem;
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  margin-top: 2rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
`;

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const DriverPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageContainer>
      <Navbar />
      <HeroSection>
        <ContentWrapper>
          <Title
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {t('driver.title', 'Drive with Wassel')}
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {t('driver.subtitle', 'Earn money on your schedule with flexible hours and competitive pay')}
          </Subtitle>
        </ContentWrapper>
      </HeroSection>

      <BenefitsSection>
        <BenefitsContainer>
          <SectionTitle>{t('driver.benefits', 'Benefits of Driving with Wassel')}</SectionTitle>
          <BenefitsList>
            <BenefitCard
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <BenefitIcon>💰</BenefitIcon>
              <BenefitTitle>{t('driver.earnings', 'Competitive Earnings')}</BenefitTitle>
              <BenefitDescription>
                {t('driver.earningsDesc', 'Earn competitive rates with bonuses during high-demand periods. Get paid weekly directly to your bank account.')}
              </BenefitDescription>
            </BenefitCard>

            <BenefitCard
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <BenefitIcon>⏰</BenefitIcon>
              <BenefitTitle>{t('driver.flexibility', 'Flexible Hours')}</BenefitTitle>
              <BenefitDescription>
                {t('driver.flexibilityDesc', 'Work when you want, as much as you want. You\'re your own boss with complete schedule flexibility.')}
              </BenefitDescription>
            </BenefitCard>

            <BenefitCard
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <BenefitIcon>🛡️</BenefitIcon>
              <BenefitTitle>{t('driver.support', '24/7 Support')}</BenefitTitle>
              <BenefitDescription>
                {t('driver.supportDesc', 'Access our dedicated support team whenever you need assistance, day or night.')}
              </BenefitDescription>
            </BenefitCard>
          </BenefitsList>
        </BenefitsContainer>
      </BenefitsSection>

      <RegistrationSection>
        <RegistrationContainer>
          <SectionTitle>{t('driver.joinUs', 'Join Our Team of Drivers')}</SectionTitle>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: 'center' }}
          >
            <RegisterButton
              href="/register"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('driver.register', 'Register as a Driver')}
            </RegisterButton>
          </motion.div>
        </RegistrationContainer>
      </RegistrationSection>

      <Footer />
    </PageContainer>
  );
};

export default DriverPage; 